import { useState } from "react";
import type { Route } from "../routes.ts";
import { HELP, NEWS, type NewsPost } from "./content.ts";
import { COMPANY, DRAFT_NOTICE, type LegalDoc } from "./legal.ts";
import { LiveMapBand } from "./LiveMap.tsx";
import { packageText, presetArt, useTripTypes, weightText, type TripType } from "./trip-types.ts";

/** Store links go live with the store listings; until then the badges say "Em breve". */
export const STORES = {
  rider: { ios: null as string | null, android: null as string | null },
  driver: { ios: null as string | null, android: null as string | null },
};

/** Stroke icons, 24×24, drawn in currentColor. */
const ICONS = {
  check: <path d="M5 12l5 5L20 7" />,
  car: <><path d="M3 16v-4l2.2-5h13.6L21 12v4z" /><path d="M3 12h18" /><circle cx="7.5" cy="16" r="1.8" /><circle cx="16.5" cy="16" r="1.8" /></>,
  person: <><circle cx="12" cy="8" r="4" /><path d="M4 21c0-4 4-6 8-6s8 2 8 6" /></>,
  box: <><path d="M3 7.5L12 3l9 4.5v9L12 21l-9-4.5z" /><path d="M3 7.5L12 12l9-4.5M12 12v9" /></>,
  shield: <><path d="M12 3l8 3v6c0 4.5-3.4 8.2-8 9-4.6-.8-8-4.5-8-9V6z" /><path d="M8.5 12l2.5 2.5 4.5-5" /></>,
  share: <><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><path d="M8.6 10.5l6.8-4M8.6 13.5l6.8 4" /></>,
  key: <><rect x="4" y="10" width="16" height="10" rx="2.5" /><path d="M8 10V7a4 4 0 0 1 8 0v3" /><path d="M12 14v2" /></>,
  sos: <><path d="M12 3v4M12 17v4M3 12h4M17 12h4" /><circle cx="12" cy="12" r="5" /></>,
  arrow: <path d="M5 12h14M13 6l6 6-6 6" />,
  phone: <><rect x="6" y="2" width="12" height="20" rx="3" /><path d="M11 18h2" /></>,
};

function Icon({ name, size = 24 }: { name: keyof typeof ICONS; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {ICONS[name]}
    </svg>
  );
}

/** The main menu: every entry is its own page (routes.ts). */
const NAV = [
  { page: "driver", href: "/motorista", label: "Motorista" },
  { page: "rider", href: "/passageiro", label: "Passageiro" },
  { page: "about", href: "/sobre-nos", label: "Sobre nós" },
  { page: "news", href: "/noticias", label: "Notícias" },
  { page: "help", href: "/ajuda", label: "Ajuda" },
] as const;

export function SiteShell({ current, children }: { current?: Route["page"]; children: React.ReactNode }) {
  return (
    <div className="site">
      <header className="site-top">
        <a className="site-brand" href="/" aria-label="ichegou, página inicial"><img src="/logo.png" alt="" /></a>
        <nav className="site-nav" aria-label="Principal">
          {NAV.map((n) => <a key={n.page} href={n.href} aria-current={n.page === current ? "page" : undefined}>{n.label}</a>)}
        </nav>
        <a className="site-nav-cta" href="/#baixar">Baixar o app</a>
      </header>
      <main>{children}</main>
      <footer className="site-foot">
        <div className="site-foot-inner">
          <div className="site-foot-top">
            <div className="site-foot-brand">
              <img className="site-brand-img" src="/logo.png" alt="ichegou" />
              <p>Corridas e envios com preço fechado, pagos no Pix. Feito para o trânsito brasileiro.</p>
            </div>
            <nav className="site-foot-cols" aria-label="Rodapé">
              <div>
                <b>ichegou</b>
                {NAV.slice(0, 4).map((n) => <a key={n.page} href={n.href}>{n.label}</a>)}
              </div>
              <div>
                <b>Ajuda</b>
                <a href="/ajuda">Central de ajuda</a>
                <a href={`mailto:${COMPANY.supportEmail}`}>Fale com a gente</a>
                <a href="/privacidade">Privacidade</a>
                <a href="/termos">Termos de uso</a>
              </div>
            </nav>
          </div>
          <div className="site-foot-legal">
            <span>© 2026 ichegou · CNPJ {COMPANY.cnpj}</span>
            <span>Feito no Brasil</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

export function Landing({ tripTypes }: { tripTypes?: TripType[] }) {
  const types = useTripTypes(tripTypes);
  return (
    <SiteShell>
      <section className="hero" id="topo">
        <div className="hero-text">
          <h1>Pediu,<br /><span className="hero-mark">ichegou.<svg viewBox="0 0 520 28" preserveAspectRatio="none" aria-hidden="true"><path d="M6 19C110 5 250 3 514 13" /></svg></span></h1>
          <p className="lede">De moto ou de Carro, com os preços mais flexíveis do mercado, você chega ao seu destino num piscar de olho.</p>
          <div className="hero-ctas">
            <a className="btn btn-primary" href="#baixar">Baixar o app</a>
            <a className="btn btn-ghost" href="/motorista">Para Motoristas</a>
          </div>
          <ul className="hero-facts">
            <li><b>Preço acessível</b><span>Ganhe mais e pague menos</span></li>
            <li><b>Segurança</b><span>Compartilhe a sua corrida</span></li>
          </ul>
        </div>
        <HeroScene />
      </section>

      <LiveMapBand />

      <Categories types={types} />

      <section className="band doors">
        <a className="door" href="/passageiro">
          <span className="eyebrow">Passageiro</span>
          <h2>Vá de moto, de carro ou mande um pacote</h2>
          <p>Veja como pedir, como pagar e o que protege você em cada corrida.</p>
          <span className="door-go">Conhecer<Icon name="arrow" size={22} /></span>
        </a>
        <a className="door door-driver" href="/motorista">
          <span className="eyebrow">Motorista</span>
          <h2>Dirija quando quiser e receba por Pix</h2>
          <p>Veja como funcionam os repasses e faça seu cadastro pelo site.</p>
          <span className="door-go">Conhecer<Icon name="arrow" size={22} /></span>
        </a>
      </section>

      <Download title={<>Baixe o ichegou.<br /><span className="brasa">Dois apps, um para cada lado da corrida.</span></>}>
        <RiderApp />
        <DriverApp />
      </Download>
    </SiteShell>
  );
}

function PageHead({ eyebrow, title, lede, children }: { eyebrow: string; title: string; lede: string; children?: React.ReactNode }) {
  return (
    <section className="page-head">
      <span className="eyebrow">{eyebrow}</span>
      <h1>{title}</h1>
      <p className="lede">{lede}</p>
      {children}
    </section>
  );
}

export function RiderPage({ tripTypes }: { tripTypes?: TripType[] }) {
  const types = useTripTypes(tripTypes);
  const deliveries = types?.filter((t) => t.service === "delivery") ?? [];
  return (
    <SiteShell current="rider">
      <PageHead eyebrow="Passageiro" title="Para onde você quiser, com o preço na frente" lede="Você vê o valor antes de pedir, escolhe como pagar e acompanha o motorista no mapa em tempo real.">
        <div className="hero-ctas"><a className="btn btn-primary" href="#baixar">Baixar o app</a></div>
      </PageHead>

      <Categories types={types && types.filter((t) => t.service === "ride")} />

      {deliveries.length > 0 && (
        <section id="envios" className="band">
          <div className="envios">
            <div className="envios-text">
              <span className="eyebrow">Envios</span>
              <h2>Também levamos seus pacotes</h2>
              <p>Foto na coleta e na entrega e um código para quem recebe. Você acompanha tudo pelo mapa.</p>
            </div>
            <ul className="envios-types">
              {deliveries.map((t) => (
                <li key={t.id}>
                  <TripTypeArt type={t} />
                  <b>{t.name}</b>
                  {t.description && <span>{t.description}</span>}
                  {packageText(t) && <span>{packageText(t)}</span>}
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      <section className="steps-band">
        <div className="band">
          <h2>Três toques e você já está a caminho</h2>
          <ol className="how-steps">
            <li><span className="step-n">01</span><h3>Diga para onde vai</h3><p>Digite o destino ou escolha um dos seus lugares salvos e veja todas as opções lado a lado.</p></li>
            <li><span className="step-n">02</span><h3>Veja o preço e pague no Pix</h3><p>O valor aparece antes de você pedir e é o que você paga. Sem taxa escondida no fim.</p></li>
            <li><span className="step-n">03</span><h3>Confira o código e embarque</h3><p>O motorista confirma seu código de 4 dígitos antes de começar. Você só entra no carro certo.</p></li>
          </ol>
        </div>
      </section>

      <section id="seguranca" className="band safety">
        <div className="safety-text">
          <span className="eyebrow">Segurança</span>
          <h2>Você chega bem. Quem te espera fica tranquilo.</h2>
          <p>Cada viagem tem proteção ligada por padrão, para passageiros e motoristas.</p>
        </div>
        <ul className="safety-cards">
          <SafetyCard icon="key" title="Código de embarque" body="A corrida só começa quando o motorista confirma o código de 4 dígitos que aparece no seu app." />
          <SafetyCard icon="share" title="Viagem compartilhada" body="Seus contatos de confiança acompanham o trajeto ao vivo, e avisamos se a rota mudar." />
          <SafetyCard icon="sos" title="Emergência" body="Ligue 190 ou alerte a central do ichegou direto da corrida, a qualquer hora." />
          <SafetyCard icon="shield" title="Motoristas verificados" body="CNH com EAR e documento do veículo conferidos antes da primeira corrida." />
        </ul>
      </section>

      <Download title="Baixe o ichegou"><RiderApp /></Download>
    </SiteShell>
  );
}

export function DriverPage() {
  return (
    <SiteShell current="driver">
      <section className="band page-top">
        <div className="drive">
          <div className="drive-text">
            <span className="eyebrow">Motorista</span>
            <h1>Seu carro. Sua agenda. Seu dinheiro.</h1>
            <ul className="driver-checks">
              <li><Icon name="check" size={22} />Repasse por Pix toda terça, ou saque na hora quando precisar</li>
              <li><Icon name="check" size={22} />100% das gorjetas e da taxa pet são suas</li>
              <li><Icon name="check" size={22} />Aceite envios e pets se quiser, e use o filtro de destino para voltar para casa</li>
            </ul>
            <a className="btn btn-dark" href="/motorista/cadastro">Quero dirigir</a>
          </div>
          <EarningsMock />
        </div>
      </section>

      <section className="steps-band">
        <div className="band">
          <h2>Como começar</h2>
          <ol className="how-steps">
            <li><span className="step-n">01</span><h3>Faça seu cadastro</h3><p><a className="reg-inline" href="/motorista/cadastro">Aqui pelo site</a> ou no app ichegou Motorista, com seu celular.</p></li>
            <li><span className="step-n">02</span><h3>Envie seus documentos</h3><p>CNH com EAR e o documento do veículo, pelo app.</p></li>
            <li><span className="step-n">03</span><h3>Comece a dirigir</h3><p>Aprovado o cadastro, faça a prova de vida no app e fique online.</p></li>
          </ol>
        </div>
      </section>

      <section className="band safety">
        <div className="safety-text">
          <span className="eyebrow">No seu ritmo</span>
          <h2>Você decide como trabalhar</h2>
          <p>Escolha as corridas que fazem sentido para você e saiba com quem está falando antes de cada embarque.</p>
        </div>
        <ul className="safety-cards">
          <SafetyCard icon="box" title="Envios e pets" body="Só recebe esses pedidos quem quer. Ligue ou desligue no app." />
          <SafetyCard icon="car" title="Filtro de destino" body="Indo para casa? Use o filtro e pegue corridas no seu caminho." />
          <SafetyCard icon="phone" title="Pagar na hora" body="Aceite dinheiro ou Pix no fim da corrida, ou desligue a opção. Se o passageiro não pagar, você recebe mesmo assim." />
          <SafetyCard icon="key" title="Código de embarque" body="A corrida só começa com o código de 4 dígitos do passageiro, e a central fica a um toque." />
        </ul>
      </section>

      <Download title="Baixe o ichegou Motorista"><DriverApp /></Download>
    </SiteShell>
  );
}

export function AboutPage() {
  return (
    <SiteShell current="about">
      <PageHead eyebrow="Sobre nós" title="Um jeito mais claro de se mover pela cidade" lede="O ichegou é um app brasileiro de corridas e envios. Conectamos quem precisa chegar a motoristas parceiros, com o preço fechado antes do pedido e o repasse por Pix." />

      <section className="band">
        <h2>No que a gente acredita</h2>
        <ol className="how-steps">
          <li><span className="brasa"><Icon name="check" size={30} /></span><h3>O preço vem antes</h3><p>Você vê quanto vai pagar antes de pedir, e esse é o valor da corrida.</p></li>
          <li><span className="brasa"><Icon name="shield" size={30} /></span><h3>Segurança ligada por padrão</h3><p>Código de embarque, viagem compartilhada e emergência em toda corrida, sem precisar configurar nada.</p></li>
          <li><span className="brasa"><Icon name="person" size={30} /></span><h3>Quem dirige recebe o que é seu</h3><p>Gorjetas e taxa pet são 100% do motorista, e os cupons ficam por nossa conta.</p></li>
        </ol>
      </section>

      <section className="band page-end">
        <div className="company">
          <h2>Fale com o ichegou</h2>
          <dl>
            <div><dt>Ajuda</dt><dd><a href={`mailto:${COMPANY.supportEmail}`}>{COMPANY.supportEmail}</a></dd></div>
            <div><dt>Privacidade</dt><dd><a href={`mailto:${COMPANY.privacyEmail}`}>{COMPANY.privacyEmail}</a></dd></div>
          </dl>
        </div>
      </section>
    </SiteShell>
  );
}

export function NewsPage({ posts = NEWS }: { posts?: NewsPost[] }) {
  return (
    <SiteShell current="news">
      <PageHead eyebrow="Notícias" title="Novidades do ichegou" lede="Cidades novas, recursos do app e mudanças importantes para passageiros e motoristas." />
      <section className="band page-end">
        {posts.length === 0 ? (
          <p className="news-empty">Ainda não publicamos nenhuma novidade. Volte em breve.</p>
        ) : (
          <ul className="news">
            {posts.map((p) => (
              <li key={p.id} id={p.id}>
                <time dateTime={p.date}>{new Date(`${p.date}T12:00:00Z`).toLocaleDateString("pt-BR", { day: "numeric", month: "long", year: "numeric" })}</time>
                <h2>{p.title}</h2>
                <p>{p.body}</p>
              </li>
            ))}
          </ul>
        )}
      </section>
    </SiteShell>
  );
}

export function HelpPage() {
  return (
    <SiteShell current="help">
      <PageHead eyebrow="Ajuda" title="Como podemos ajudar?" lede="As dúvidas mais comuns de passageiros e motoristas. Em perigo durante uma corrida, ligue 190." />
      <section className="band page-end help">
        {HELP.map((topic) => (
          <section key={topic.id} id={topic.id} className="help-topic">
            <h2>{topic.title}</h2>
            <div className="faq">
              {topic.items.map((item) => (
                <details key={item.q}>
                  <summary>{item.q}</summary>
                  <p>{item.a}</p>
                </details>
              ))}
            </div>
          </section>
        ))}
        <div className="company">
          <h2>Não achou o que procurava?</h2>
          <p>Escreva para a gente. Se for sobre uma corrida, conte o dia e o horário.</p>
          <a className="btn btn-dark" href={`mailto:${COMPANY.supportEmail}`}>{COMPANY.supportEmail}</a>
        </div>
      </section>
    </SiteShell>
  );
}

function Download({ title, children }: { title: React.ReactNode; children: React.ReactNode }) {
  return (
    <section id="baixar" className="band">
      <div className="download">
        <h2>{title}</h2>
        <div className="apps">{children}</div>
      </div>
    </section>
  );
}

const RiderApp = () => <AppRow name="ichegou" icon="/app-icon-rider.png" who="Para passageiros e envios" stores={STORES.rider} />;
export const DriverApp = () => <AppRow name="ichegou Motorista" icon="/app-icon-driver.png" who="Para motoristas parceiros" stores={STORES.driver} />;

/** The rider app itself, captured by tools/brand/site_assets.py, with the route drawn behind it. */
function HeroScene() {
  return (
    <div className="hero-scene">
      <svg className="hero-route" viewBox="0 0 600 680" aria-hidden="true">
        <path d="M566 26C492 96 590 214 520 300S330 430 250 512 120 606 86 618" />
        <circle cx="566" cy="26" r="13" />
      </svg>
      <figure className="phone">
        <img src="/app/inicio.webp" alt="Tela inicial do app ichegou: Para onde vamos?, com o mapa e os serviços" width={960} height={2087} />
        <span className="phone-island" aria-hidden="true" />
      </figure>
      <img className="hero-car" src="/app/carro.webp" alt="" width={720} height={714} />
      <img className="hero-code" src="/app/codigo-de-embarque.webp" alt="Código de embarque no app, para informar ao motorista" width={960} height={156} />
    </div>
  );
}

/** The icon uploaded in admin, else the 3D drawing of the type's preset. */
function TripTypeArt({ type }: { type: TripType }) {
  const [broken, setBroken] = useState(false);
  return <img className="type-art" src={!broken && type.iconUrl ? type.iconUrl : presetArt(type)} alt="" width={256} height={256} onError={() => setBroken(true)} />;
}

/** Prices stay in the app: they change with demand. Null `types` holds the cards' place while they load. */
function Categories({ types }: { types: TripType[] | null }) {
  if (types?.length === 0) return null;
  return (
    <section className="band">
      <div className="band-head split">
        <h2>Escolha o seu jeito de viajar</h2>
        <p>Do rolê de sexta ao aeroporto às 5 da manhã</p>
      </div>
      {types ? (
        <ul className="cats">
          {types.map((t) => (
            <li key={t.id} className="cat">
              <span className="cat-top">
                <TripTypeArt type={t} />
                {t.service === "ride"
                  ? <span className="cat-seats"><Icon name="person" size={16} />{t.seats}</span>
                  : t.maxWeightKg !== null && <span className="cat-seats"><Icon name="box" size={16} />{weightText(t.maxWeightKg)}</span>}
              </span>
              <span className="cat-name">{t.name}{t.badge && <span className="cat-badge">{t.badge}</span>}</span>
              {t.description && <span className="cat-desc">{t.description}</span>}
            </li>
          ))}
        </ul>
      ) : (
        <ul className="cats" aria-hidden="true">
          {[0, 1, 2, 3].map((i) => <li key={i} className="cat cat-loading" />)}
        </ul>
      )}
    </section>
  );
}

function SafetyCard({ icon, title, body }: { icon: keyof typeof ICONS; title: string; body: string }) {
  return (
    <li className="safety-card">
      <span className="brasa"><Icon name={icon} size={30} /></span>
      <h3>{title}</h3>
      <p>{body}</p>
    </li>
  );
}

/** The driver app's weekly earnings card, drawn in CSS. */
function EarningsMock() {
  const bars = [40, 62, 48, 80, 100, 70, 30];
  return (
    <figure className="earnings" aria-label="Exemplo do app ichegou Motorista: ganhos da semana">
      <div className="earn-head"><span>Ganhos da semana</span><span className="earn-tag">Exemplo</span></div>
      <div className="earn-bars" aria-hidden="true">
        {bars.map((h, i) => <span key={i} className={i === 4 ? "on" : undefined} style={{ height: `${h}%` }} />)}
      </div>
      <div className="earn-days" aria-hidden="true">{["S", "T", "Q", "Q", "S", "S", "D"].map((d, i) => <span key={i}>{d}</span>)}</div>
      <div className="earn-row"><span>Próximo repasse</span><b>terça, no Pix</b></div>
      <div className="earn-row"><span>Sacar agora</span><span className="brasa"><Icon name="arrow" size={20} /></span></div>
    </figure>
  );
}

function AppRow({ name, icon, who, stores }: { name: string; icon: string; who: string; stores: { ios: string | null; android: string | null } }) {
  return (
    <div className="app-row">
      <img className="app-icon" src={icon} alt="" />
      <div className="app-name"><b>{name}</b><span>{who}</span></div>
      <div className="badges">
        <StoreBadge store="App Store" href={stores.ios} />
        <StoreBadge store="Google Play" href={stores.android} />
      </div>
    </div>
  );
}

/** The stores' own logos, in one colour (Simple Icons, CC0). */
const STORE_LOGOS = {
  "App Store": "M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701",
  "Google Play": "M22.018 13.298l-3.919 2.218-3.515-3.493 3.543-3.521 3.891 2.202a1.49 1.49 0 0 1 0 2.594zM1.337.924a1.486 1.486 0 0 0-.112.568v21.017c0 .217.045.419.124.6l11.155-11.087L1.337.924zm12.207 10.065l3.258-3.238L3.45.195a1.466 1.466 0 0 0-.946-.179l11.04 10.973zm0 2.067l-11 10.933c.298.036.612-.016.906-.183l13.324-7.54-3.23-3.21z",
};

function StoreBadge({ store, href }: { store: keyof typeof STORE_LOGOS; href: string | null }) {
  const logo = <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d={STORE_LOGOS[store]} /></svg>;
  const label = <span className="badge-text"><small>{href ? "Baixar na" : "Em breve"}</small>{store}</span>;
  if (href) return <a className="badge" href={href}>{logo}{label}</a>;
  return <span className="badge soon" aria-label={`${store}: em breve`}>{logo}{label}</span>;
}

export function Legal({ doc }: { doc: LegalDoc }) {
  return (
    <SiteShell>
      <article className="legal">
        <p className="draft" role="note">{DRAFT_NOTICE}. Este texto ainda não vale como política oficial do ichegou.</p>
        <h1>{doc.title}</h1>
        <p className="muted">Versão de {new Date(`${doc.updatedAt}T12:00:00Z`).toLocaleDateString("pt-BR", { day: "numeric", month: "long", year: "numeric" })}</p>
        <p className="lede">{doc.intro}</p>
        <nav className="toc" aria-label="Nesta página">
          {doc.sections.map((s) => <a key={s.id} href={`#${s.id}`}>{s.heading}</a>)}
        </nav>
        {doc.sections.map((s) => (
          <section key={s.id} id={s.id}>
            <h2>{s.heading}</h2>
            {s.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
            {s.items && <ul>{s.items.map((it, i) => <li key={i}>{it}</li>)}</ul>}
          </section>
        ))}
      </article>
    </SiteShell>
  );
}
